interface ValidationResult {
  isValid: boolean;
  message: string;
}

class PasswordValidator {
  private static weakPasswords = new Set([
    'password',
    'qwerty',
    'abc123',
    'password123',
    'admin',
    'letmein',
    'welcome',
    '1111',
    '0000',
    '123123',
    '1234',
    '12345',
    '123456',
    '1234567',
    '12345678',
    '123456789',
    '1234567890',
  ]);

  private static commonWords = new Set([
    'password',
    'welcome',
    'computer',
    'internet',
    'security',
    'love',
    'money',
    'admin',
    'user',
    'test',
    'hello',
    'world',
    'system',
    'login',
    'access',
  ]);

  /**
   * Validates password based on security requirements:
   * - Minimum 8 characters
   * - At least one uppercase letter
   * - At least one lowercase letter
   * - At least one digit
   * - At least one special character
   * - No common weak passwords
   */
  public static validatePassword(password: string): ValidationResult {
    // Check if password is in weak passwords list (case insensitive)
    if (this.weakPasswords.has(password.toLowerCase())) {
      return {
        isValid: false,
        message: 'Password is too common and weak',
      };
    }

    // Main regex pattern for password validation
    // (?=.*[a-z]) - at least one lowercase letter
    // (?=.*[A-Z]) - at least one uppercase letter
    // (?=.*\d) - at least one digit
    // (?=.*[@$!%*?&]) - at least one special character
    // .{8,} - minimum 8 characters
    const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (pattern.test(password)) {
      // Additional check: prevent single dictionary words
      // Remove special characters and numbers to check base word
      const baseWord = password.replace(/[^a-zA-Z]/g, '').toLowerCase();
      if (this.commonWords.has(baseWord)) {
        return {
          isValid: false,
          message: 'Password should not be based on common dictionary words',
        };
      }

      return {
        isValid: true,
        message: 'Password is valid',
      };
    } else {
      return {
        isValid: false,
        message:
          'Password must be at least 8 characters with uppercase, lowercase, number, and special character',
      };
    }
  }

  /**
   * Alternative method with separate validation checks for more detailed feedback
   */
  public static validatePasswordDetailed(password: string): ValidationResult {
    const errors: string[] = [];

    // Check length
    if (password.length < 8) {
      errors.push('at least 8 characters');
    }

    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      errors.push('at least one lowercase letter');
    }

    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors.push('at least one uppercase letter');
    }

    // Check for digit
    if (!/\d/.test(password)) {
      errors.push('at least one number');
    }

    // Check for special character
    if (!/[@$!%*?&]/.test(password)) {
      errors.push('at least one special character (@$!%*?&)');
    }

    // Check for weak passwords
    if (this.weakPasswords.has(password.toLowerCase())) {
      errors.push('not be a common weak password');
    }

    // Check for dictionary words
    const baseWord = password.replace(/[^a-zA-Z]/g, '').toLowerCase();
    if (this.commonWords.has(baseWord)) {
      errors.push('not be based on common dictionary words');
    }

    if (errors.length === 0) {
      return {
        isValid: true,
        message: 'Password is valid',
      };
    } else {
      return {
        isValid: false,
        message: `Password must have: ${errors.join(', ')}`,
      };
    }
  }
}

export { PasswordValidator, type ValidationResult };
